import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { exportToExcel } from '../../helpers/exportFuncs';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import { getComparator, Order, sortedRowInformation } from '../../helpers/sortingsFuncs';
import LoadingSpinner from '../LoadingSpinner';
import { visuallyHidden } from '@mui/utils';

interface HeaderMappingItem {
  name: string;
  type: string;
  digits: number;
  attach: string;
}

interface CustomerStatusRoiData {
  customer_status: string;
  leads: number;
  soi_leads: number;
  total_cost: number;
  revenue_30d: number;
  revenue_total: number;
}

const headerMapping: Record<string, HeaderMappingItem> = {
  customer_status: {
    name: 'Kundenstatus',
    type: 'string',
    digits: 0,
    attach: '',
  },
  leads: {
    name: 'Leads (DOI)',
    type: 'number',
    digits: 0,
    attach: '',
  },
  soi_leads: {
    name: 'Leads (SOI)',
    type: 'number',
    digits: 0,
    attach: '',
  },
  revenue_30d: {
    name: 'Revenue 30d',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  revenue_total: {
    name: 'Revenue Total',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  total_cost: {
    name: 'Costs',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  roi30: {
    name: 'ROI 30d',
    type: 'number',
    digits: 2,
    attach: '%',
  },
  roi_total: {
    name: 'ROI Total',
    type: 'number',
    digits: 2,
    attach: '%',
  },
};

type Data = Record<DataKey, string>;
type DataKey = keyof CustomerStatusRoiData;

const RoiPerCustomerStatus: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const [data, setData] = useState<CustomerStatusRoiData[]>([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof CustomerStatusRoiData>('leads');

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof CustomerStatusRoiData);
  };

  const createSortHandler = (property: keyof Data) => () => {
    handleRequestSort(property);
  };

  const computeSummary = (
    data: CustomerStatusRoiData[],
  ): CustomerStatusRoiData & { roi30: number; roi_total: number } => {
    if (!data.length) {
      return {
        customer_status: 'Insgesamt',
        leads: 0,
        soi_leads: 0,
        total_cost: 0,
        revenue_30d: 0,
        revenue_total: 0,
        roi30: 0,
        roi_total: 0,
      };
    }

    const summary = {
      customer_status: 'Insgesamt',
      leads: 0,
      soi_leads: 0,
      total_cost: 0,
      revenue_30d: 0,
      revenue_total: 0,
      roi30: 0,
      roi_total: 0,
    };

    data.forEach((item) => {
      summary.leads += parseInt(String(item.leads)) || 0;
      summary.soi_leads += parseInt(String(item.soi_leads)) || 0;
      summary.total_cost += parseFloat(String(item.total_cost)) || 0;
      summary.revenue_30d += parseFloat(String(item.revenue_30d)) || 0;
      summary.revenue_total += parseFloat(String(item.revenue_total)) || 0;
    });

    // Calculate ROI percentages
    summary.roi30 = summary.total_cost > 0 ? summary.revenue_30d / summary.total_cost : 0;
    summary.roi_total = summary.total_cost > 0 ? summary.revenue_total / summary.total_cost : 0;

    return summary;
  };

  useEffect(() => {
    if (!filterState.startDate || !filterState.endDate) return;

    const networks = Array.isArray(filterState.network)
      ? filterState.network
      : [filterState.network];
    const themes = Array.isArray(filterState.theme)
      ? filterState.theme
      : filterState.theme
        ? [filterState.theme]
        : [];
    const requestData = {
      start_date: dateToSqlDate(filterState.startDate),
      end_date: dateToSqlDate(filterState.endDate),
      network: networks,
      ...(themes.length > 0 && { theme: themes }),
      ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
    };

    setLoading(true);
    fetchData('/api/vnr/roi-per-customer-status', requestData)
      .then((result) => {
        // Convert string values to numbers for proper sorting
        const resultArray = Array.isArray(result) ? result : [result];
        const convertedData = resultArray.map((item: any) => ({
          ...item,
          leads: parseInt(item.leads) || 0,
          soi_leads: parseInt(item.soi_leads) || 0,
          total_cost: parseFloat(item.total_cost) || 0,
          revenue_30d: parseFloat(item.revenue_30d) || 0,
          revenue_total: parseFloat(item.revenue_total) || 0,
        }));
        setData(convertedData);
      })
      .catch((error) => {
        console.error('Error fetching customer status ROI data:', error);
        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    filterState.startDate,
    filterState.endDate,
    filterState.network,
    filterState.theme,
    filterState.vnrVerlag,
  ]);

  const sortedRows = sortedRowInformation(data, getComparator(order, orderBy));
  const summary = computeSummary(data);

  const formatLeadsWithPercentage = (value: number, total: number): React.ReactElement => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return (
      <>
        <strong>{formatNumber(value, 0)}</strong> | {formatNumber(percentage, 2)}%
      </>
    );
  };

  const exportData = () => {
    exportToExcel([...sortedRows, computeSummary(data)], 'roi-per-customer-status');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
        <h2>VNR ROI nach Kundenstatus</h2>
        <Button variant="contained" onClick={exportData}>
          Export to Excel
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {Object.keys(headerMapping).map((key) => (
                <TableCell key={key}>
                  <TableSortLabel
                    active={orderBy === key}
                    direction={orderBy === key ? order : 'asc'}
                    onClick={createSortHandler(key as keyof CustomerStatusRoiData)}
                  >
                    {headerMapping[key].name}
                    {orderBy === key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.customer_status}</TableCell>
                <TableCell>{formatLeadsWithPercentage(row.leads, summary.leads)}</TableCell>
                <TableCell>{formatLeadsWithPercentage(row.soi_leads, summary.soi_leads)}</TableCell>
                <TableCell>{formatNumber(row.revenue_30d, 2) + '€'}</TableCell>
                <TableCell>{formatNumber(row.revenue_total, 2) + '€'}</TableCell>
                <TableCell>{formatNumber(row.total_cost, 2) + '€'}</TableCell>
                <TableCell>
                  {formatNumber(
                    parseFloat(String(row.total_cost)) > 0
                      ? (parseFloat(String(row.revenue_30d)) / parseFloat(String(row.total_cost))) *
                          100
                      : 0,
                    2,
                  ) + '%'}
                </TableCell>
                <TableCell>
                  {formatNumber(
                    parseFloat(String(row.total_cost)) > 0
                      ? (parseFloat(String(row.revenue_total)) /
                          parseFloat(String(row.total_cost))) *
                          100
                      : 0,
                    2,
                  ) + '%'}
                </TableCell>
              </TableRow>
            ))}
            {/* Summary Row */}
            {data.length > 0 && (
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.1)', fontWeight: 'bold' }}>
                {(() => {
                  const summary = computeSummary(data);
                  return (
                    <>
                      <TableCell sx={{ fontWeight: 'bold' }}>{summary.customer_status}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatNumber(summary.leads, 0)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatNumber(summary.soi_leads, 0)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatNumber(summary.revenue_30d, 2) + '€'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatNumber(summary.revenue_total, 2) + '€'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatNumber(summary.total_cost, 2) + '€'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatNumber(summary.roi30 * 100, 2) + '%'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatNumber(summary.roi_total * 100, 2) + '%'}
                      </TableCell>
                    </>
                  );
                })()}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RoiPerCustomerStatus;
